/**
 * ResourceAssignmentsPanel — Phase 3 of Workspace Overview.
 *
 * Lets consultants assign actual named people to each role on a given
 * estimate, with percentage splits. Replaces the divide-equally
 * fallback for any role with explicit assignments. Sits inside the
 * Planner > Resources tab.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Loader2, Users, Plus, Trash2, AlertTriangle, Check, Save,
  Briefcase, UserPlus, Info, Copy, ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// A roster member can now fill multiple project roles. Fall back to the
// legacy single `role` for records saved before multi-role support.
const resRoles = (p) => (p?.roles && p.roles.length ? p.roles : (p?.role ? [p.role] : []));
const canFill = (p, role) => resRoles(p).includes(role);

// Local row uses a stable react key so rapid edits don't lose focus on
// the percentage input. We strip it before PUT.
let _rowSeq = 0;
const newRowKey = () => `row_${++_rowSeq}_${Date.now()}`;

export default function ResourceAssignmentsPanel({ estimateId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [roster, setRoster] = useState([]);
  const [roleLabels, setRoleLabels] = useState({});
  const [roleHours, setRoleHours] = useState({});
  const [rows, setRows] = useState([]); // [{key, role, resource_id, percentage}]
  const [dirty, setDirty] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copySource, setCopySource] = useState([]);
  const [copyOverwrite, setCopyOverwrite] = useState(true);
  const [copying, setCopying] = useState(false);
  // Person-centric bulk assign (one person → many roles in one step)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignPersonId, setAssignPersonId] = useState('');
  const [assignSel, setAssignSel] = useState({}); // { [role]: { selected, grant, pct } }
  const [assignBusy, setAssignBusy] = useState(false);
  const [focusPersonId, setFocusPersonId] = useState(null); // audit-one-person filter
  const rolesRef = React.useRef(null);

  const fetchAll = useCallback(async () => {
    if (!estimateId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API_URL}/api/estimates/${estimateId}/resource-assignments`, { withCredentials: true });
      setRoster(r.data?.roster || []);
      setRoleLabels(r.data?.role_labels || {});
      setRoleHours(r.data?.role_hours || {});
      const persisted = (r.data?.assignments || []).map(a => ({ ...a, key: newRowKey() }));
      setRows(persisted);
      setDirty(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Group editor rows by role for display. Roles with hours OR explicit
  // assignments OR with active roster members all surface as sections —
  // so admins can pre-assign roles even before hours land.
  const rolesInScope = useMemo(() => {
    const set = new Set();
    Object.keys(roleHours).forEach(k => set.add(k));
    rows.forEach(r => set.add(r.role));
    roster.forEach(r => resRoles(r).forEach(rc => set.add(rc)));
    // Deterministic order: roles with hours first (descending), then alpha
    return Array.from(set).sort((a, b) => {
      const ha = roleHours[a] || 0;
      const hb = roleHours[b] || 0;
      if (hb !== ha) return hb - ha;
      return a.localeCompare(b);
    });
  }, [roleHours, rows, roster]);

  const rowsByRole = useMemo(() => {
    const out = {};
    for (const r of rows) {
      if (!out[r.role]) out[r.role] = [];
      out[r.role].push(r);
    }
    return out;
  }, [rows]);

  const totalByRole = useMemo(() => {
    const out = {};
    for (const r of rows) {
      out[r.role] = (out[r.role] || 0) + (Number(r.percentage) || 0);
    }
    return out;
  }, [rows]);

  // Reverse view: roll rows up per person so you can eyeball everyone's
  // multi-role load on THIS estimate at a glance (before saving).
  // Over-allocation is DATE-AWARE: `pct` is the person's PEAK concurrent
  // FTE across overlapping assignment windows (whole-project rows count at
  // all times), so two non-overlapping windows never falsely read as >100%.
  const peopleSummary = useMemo(() => {
    const NEG = -Infinity, POS = Infinity;
    const toTs = (d, fallback) => (d ? new Date(d + 'T00:00:00').getTime() : fallback);
    const peakConcurrent = (rs) => {
      // Candidate peak times = each row's effective start (max overlap always
      // begins at some interval's start). Sum pct of every row covering it.
      let peak = 0;
      const bounds = rs.map(r => ({ s: toTs(r.start_date, NEG), e: toTs(r.end_date, POS), p: Number(r.percentage) || 0 }));
      const candidates = bounds.map(b => b.s);
      for (const t of candidates) {
        let sum = 0;
        for (const b of bounds) if (b.s <= t && t <= b.e) sum += b.p;
        if (sum > peak) peak = sum;
      }
      return peak;
    };

    const nameById = {};
    roster.forEach(p => { nameById[p.id] = p.name; });
    const byId = {};
    for (const r of rows) {
      const id = r.resource_id;
      if (!id) continue;
      if (!byId[id]) byId[id] = { id, name: nameById[id] || 'Unknown', roles: [], hours: 0, pct: 0, hasFlat: false, over: false, _rows: [] };
      const entry = byId[id];
      if (!entry.roles.includes(r.role)) entry.roles.push(r.role);
      entry._rows.push(r);
      if ((r.mode || 'planner') === 'flat') entry.hasFlat = true;
      else entry.hours += ((roleHours[r.role] || 0) * (Number(r.percentage) || 0)) / 100;
    }
    const list = Object.values(byId);
    list.forEach(e => {
      e.pct = peakConcurrent(e._rows);
      e.over = e.pct > 100 + 1e-6;
      delete e._rows;
    });
    return list.sort((a, b) => b.roles.length - a.roles.length || b.hours - a.hours);
  }, [rows, roster, roleHours]);

  const addRow = (role) => {
    const candidates = roster.filter(p => canFill(p, role) && !rows.some(r => r.role === role && r.resource_id === p.id));
    if (candidates.length === 0) {
      toast.error(`No available ${role} resources to assign — add more in Settings → Named Resources or remove an existing row.`);
      return;
    }
    const used = totalByRole[role] || 0;
    const remaining = Math.max(0, 100 - used);
    // Default split: if only one candidate, take the whole remainder; otherwise
    // split the remainder across (existing rows + this new one) evenly, with a
    // 10% minimum so users don't see a phantom 0% row when many peers exist.
    const futureRowCount = (rowsByRole[role]?.length || 0) + 1;
    const defaultPct = candidates.length === 1
      ? Math.max(remaining, 0)
      : Math.max(10, Math.min(remaining, Math.floor(remaining / Math.max(1, futureRowCount))));
    setRows(prev => [...prev, {
      key: newRowKey(),
      role,
      resource_id: candidates[0].id,
      percentage: defaultPct,
      start_date: null,
      end_date: null,
      mode: 'planner',
    }]);
    setDirty(true);
  };

  const updateRow = (key, patch) => {
    setRows(prev => prev.map(r => r.key === key ? { ...r, ...patch } : r));
    setDirty(true);
  };

  const removeRow = (key) => {
    setRows(prev => prev.filter(r => r.key !== key));
    setDirty(true);
  };

  const openCopyDialog = async () => {
    setCopyOpen(true);
    setCopyLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/named-resources/estimates-with-assignments?limit=40`, { withCredentials: true });
      // Filter out the current estimate from the source list.
      const filtered = (r.data?.estimates || []).filter(e => e.id !== estimateId);
      setCopySource(filtered);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load source estimates');
      setCopyOpen(false);
    } finally {
      setCopyLoading(false);
    }
  };

  const performCopy = async (sourceId, sourceName) => {
    if (rows.length > 0 && copyOverwrite && !window.confirm(
      `Replace this estimate's existing ${rows.length} assignment row(s) with the rows from "${sourceName}"?`
    )) return;
    setCopying(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/estimates/${estimateId}/resource-assignments/copy-from`,
        { source_estimate_id: sourceId, overwrite: copyOverwrite },
        { withCredentials: true }
      );
      const data = r.data || {};
      const applied = (data.assignments || []).map(a => ({ ...a, key: newRowKey() }));
      setRows(applied);
      setDirty(false);
      setCopyOpen(false);
      const skippedNote = data.skipped_count
        ? ` · ${data.skipped_count} row${data.skipped_count === 1 ? '' : 's'} skipped (roster changed)`
        : '';
      toast.success(`Copied ${data.applied_count} assignment${data.applied_count === 1 ? '' : 's'} from "${data.source_name}"${skippedNote}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Copy failed');
    } finally {
      setCopying(false);
    }
  };

  // ── Person-centric bulk assign ────────────────────────────────────
  const assignPerson = useMemo(
    () => roster.find(p => p.id === assignPersonId) || null,
    [roster, assignPersonId]
  );

  const openAssignDialog = () => {
    const first = roster[0];
    const pid = first?.id || '';
    setAssignPersonId(pid);
    // Pre-seed selection map for every role in scope with a sensible default %.
    const seed = {};
    for (const role of rolesInScope) {
      const remaining = Math.max(0, 100 - (totalByRole[role] || 0));
      seed[role] = { selected: false, grant: false, pct: remaining >= 1 ? Math.round(remaining) : 100 };
    }
    setAssignSel(seed);
    setAssignOpen(true);
  };

  const toggleAssignRole = (role, patch) => {
    setAssignSel(prev => ({ ...prev, [role]: { ...prev[role], ...patch } }));
  };

  const performAssignPerson = async () => {
    if (!assignPerson) { toast.error('Pick a person first.'); return; }
    const chosen = rolesInScope.filter(r => assignSel[r]?.selected);
    if (chosen.length === 0) { toast.error('Tick at least one role to assign.'); return; }

    const held = new Set(resRoles(assignPerson));
    const missingNoGrant = chosen.filter(r => !held.has(r) && !assignSel[r]?.grant);
    if (missingNoGrant.length > 0) {
      toast.error(
        `${assignPerson.name} doesn't hold: ${missingNoGrant.join(', ')}. Tick "also add to their profile" for those, or add them in Settings → Named Resources first.`,
        { duration: 7000 }
      );
      return;
    }

    setAssignBusy(true);
    try {
      // 1) Grant any missing capabilities to the person's roster profile so the
      //    assignment save validates. Persisted immediately.
      const toGrant = chosen.filter(r => !held.has(r));
      if (toGrant.length > 0) {
        const mergedRoles = Array.from(new Set([...resRoles(assignPerson), ...toGrant]));
        await axios.patch(
          `${API_URL}/api/named-resources/${assignPerson.id}`,
          { roles: mergedRoles },
          { withCredentials: true }
        );
        setRoster(prev => prev.map(p => p.id === assignPerson.id ? { ...p, roles: mergedRoles } : p));
      }

      // 2) Add editor rows for each chosen role (skip exact whole-project dupes).
      const additions = [];
      const skipped = [];
      for (const role of chosen) {
        const dupe = rows.some(o =>
          o.role === role && o.resource_id === assignPerson.id && !o.start_date && !o.end_date
        );
        if (dupe) { skipped.push(role); continue; }
        additions.push({
          key: newRowKey(),
          role,
          resource_id: assignPerson.id,
          percentage: Number(assignSel[role]?.pct) || 0,
          start_date: null,
          end_date: null,
          mode: 'planner',
        });
      }

      if (additions.length > 0) {
        setRows(prev => [...prev, ...additions]);
        setDirty(true);
      }
      setAssignOpen(false);

      const grantNote = toGrant.length ? ` · added ${toGrant.length} role${toGrant.length === 1 ? '' : 's'} to their profile` : '';
      const skipNote = skipped.length ? ` · ${skipped.length} already assigned` : '';
      if (additions.length > 0) {
        toast.success(`${assignPerson.name} added to ${additions.length} role${additions.length === 1 ? '' : 's'}${grantNote}${skipNote}. Review and Save.`, { duration: 6000 });
      } else {
        toast.info(`Nothing to add — ${assignPerson.name} is already on those roles.${grantNote}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    } finally {
      setAssignBusy(false);
    }
  };

  const handleSave = async () => {
    for (const r of rows) {
      if (!r.resource_id) {
        toast.error('Pick a resource for every row before saving.');
        return;
      }
      if (Number(r.percentage) < 0 || Number(r.percentage) > 100) {
        toast.error('FTE % must be between 0 and 100 per person.');
        return;
      }
      if (r.start_date && r.end_date && r.start_date > r.end_date) {
        toast.error('A row\'s start date is after its end date.');
        return;
      }
    }
    // Note: totals may exceed 100% (stackable) — that's a non-blocking
    // warning surfaced from the server, not a save blocker.
    setSaving(true);
    try {
      const payload = {
        assignments: rows.map(r => ({
          role: r.role,
          resource_id: r.resource_id,
          percentage: Number(r.percentage),
          start_date: r.start_date || null,
          end_date: r.end_date || null,
          mode: r.mode || 'planner',
        })),
      };
      const res = await axios.put(
        `${API_URL}/api/estimates/${estimateId}/resource-assignments`,
        payload,
        { withCredentials: true }
      );
      const warnings = res.data?.warnings || [];
      toast.success(rows.length === 0 ? 'Assignments cleared — back to divide-equally' : 'Assignments saved');
      warnings.forEach(w => toast.warning(w.message, { duration: 6000 }));
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8" data-testid="resource-assignments-loading">
          <Loader2 className="w-5 h-5 animate-spin text-[#0F2040]" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="resource-assignments-error">
        <CardContent className="py-6 text-sm text-rose-600 text-center">{error}</CardContent>
      </Card>
    );
  }

  if (roster.length === 0) {
    return (
      <Card data-testid="resource-assignments-empty">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="w-4 h-4 text-[#4ABFCF]" />
            Resource assignments
          </CardTitle>
        </CardHeader>
        <CardContent className="py-4 text-center">
          <UserPlus className="w-7 h-7 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-600 font-medium">No named resources yet.</p>
          <p className="text-xs text-slate-400 mt-1">
            Add people in <strong>Settings → Named Resources</strong>, then come back to assign them here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="resource-assignments-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-[#4ABFCF]" />
              Resource assignments
            </CardTitle>
            <p className="text-[11px] text-slate-500 mt-1">
              Pick who covers each role and what slice of the hours they take. Anything you don't assign falls back to the divide-equally model across all peers of that role.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={openAssignDialog}
              disabled={saving || copying || roster.length === 0}
              data-testid="resource-assignments-assign-person-open"
              title="Assign one person to multiple roles at once"
            >
              <UserPlus className="w-3.5 h-3.5 mr-1" />
              Assign person…
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={openCopyDialog}
              disabled={saving || copying}
              data-testid="resource-assignments-copy-open"
              title="Copy assignments from a previous estimate"
            >
              <Copy className="w-3.5 h-3.5 mr-1" />
              Copy from…
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving || !dirty}
              className="bg-[#0F2040] hover:bg-[#1A3055] text-white"
              data-testid="resource-assignments-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {rolesInScope.length === 0 ? (
          <p className="text-xs text-slate-400 italic py-4 text-center" data-testid="resource-assignments-no-roles">
            No roles in scope for this estimate yet — assignments will appear once the estimate calculates role hours.
          </p>
        ) : (
          <div className="space-y-4">
            {peopleSummary.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="resource-assignments-people-summary">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  <Users className="w-3.5 h-3.5" /> Who's on this project ({peopleSummary.length})
                  {focusPersonId && (
                    <button
                      type="button"
                      onClick={() => setFocusPersonId(null)}
                      className="ml-2 normal-case tracking-normal text-[11px] font-medium text-[#4ABFCF] hover:underline"
                      data-testid="resource-assignments-clear-focus"
                    >
                      Show all roles
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {peopleSummary.map(p => {
                    const active = focusPersonId === p.id;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => {
                          const next = active ? null : p.id;
                          setFocusPersonId(next);
                          if (next) setTimeout(() => rolesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                        }}
                        className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 transition ${
                          active ? 'bg-[#0F2040] border-[#0F2040] ring-2 ring-[#4ABFCF]/40' : p.over ? 'bg-white border-rose-300' : p.roles.length > 1 ? 'bg-white border-[#4ABFCF]' : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`resource-assignments-person-chip-${p.id}`}
                        title={`${p.name} — ${p.roles.length} role${p.roles.length === 1 ? '' : 's'}, ${Math.round(p.pct)}% peak concurrent FTE${p.over ? ' (over-allocated)' : ''}. Click to audit.`}
                      >
                        <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-[9px] font-semibold shrink-0">
                          {(p.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('')}
                        </span>
                        <span className={`text-xs font-medium whitespace-nowrap ${active ? 'text-white' : 'text-[#0F2040]'}`}>{p.name}</span>
                        <span className="flex items-center gap-1">
                          {p.roles.map(rc => (
                            <Badge key={rc} className={`text-[9px] px-1.5 py-0 ${active ? 'bg-white text-[#0F2040]' : 'bg-[#0F2040] text-white'}`}>{rc}</Badge>
                          ))}
                        </span>
                        {p.roles.length > 1 && (
                          <span className={`text-[9px] font-semibold whitespace-nowrap ${active ? 'text-[#4ABFCF]' : 'text-[#4ABFCF]'}`}>×{p.roles.length}</span>
                        )}
                        <span className={`text-[10px] font-semibold tabular-nums whitespace-nowrap px-1.5 py-0.5 rounded-full ${p.over ? 'bg-rose-100 text-rose-700' : (active ? 'bg-white/20 text-white' : 'text-slate-500')}`}>
                          {p.over && <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />}
                          {Math.round(p.pct)}%
                        </span>
                        {p.hours > 0 && (
                          <span className={`text-[11px] tabular-nums whitespace-nowrap ${active ? 'text-slate-200' : 'text-slate-500'}`}>· {Math.round(p.hours).toLocaleString()}h</span>
                        )}
                        {p.hasFlat && (
                          <span className="text-[9px] text-amber-500 whitespace-nowrap" title="Includes fixed Flat-FTE assignments (not counted in the hours estimate)">+flat</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {focusPersonId && (
              <div className="flex items-center gap-2 text-[11px] text-slate-600 bg-[#4ABFCF]/10 border border-[#4ABFCF]/30 rounded-md px-3 py-1.5" data-testid="resource-assignments-focus-banner">
                <Info className="w-3.5 h-3.5 text-[#4ABFCF]" />
                Auditing <strong>{peopleSummary.find(p => p.id === focusPersonId)?.name || 'person'}</strong> — showing only their roles.
                {peopleSummary.find(p => p.id === focusPersonId)?.over && (
                  <span className="text-rose-700 font-semibold flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Over-allocated ({Math.round(peopleSummary.find(p => p.id === focusPersonId)?.pct || 0)}% peak concurrent FTE)
                  </span>
                )}
              </div>
            )}
            <div ref={rolesRef} className="space-y-4">
            {(focusPersonId ? rolesInScope.filter(role => rows.some(r => r.role === role && r.resource_id === focusPersonId)) : rolesInScope).map(role => {
              const candidates = roster.filter(p => canFill(p, role));
              const assignedRows = (rowsByRole[role] || []).filter(r => !focusPersonId || r.resource_id === focusPersonId);
              const total = totalByRole[role] || 0;
              const hrs = roleHours[role] || 0;
              const remaining = 100 - total;
              const isOver = total > 100 + 1e-6;
              const fallbackHours = hrs * Math.max(0, remaining / 100);

              return (
                <div
                  key={role}
                  className="border border-slate-200 rounded-lg p-3 bg-white"
                  data-testid={`resource-assignments-role-${role}`}
                >
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-[#0F2040] text-white text-[10px]">{role}</Badge>
                      <span className="text-sm font-medium text-[#0F2040]">{roleLabels[role] || role}</span>
                      {hrs > 0 && (
                        <span className="text-[11px] text-slate-500">
                          {hrs.toLocaleString()}h on this estimate
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[11px] font-semibold tabular-nums px-2 py-0.5 rounded-full ${
                          isOver
                            ? 'bg-rose-100 text-rose-700'
                            : total >= 99.5
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                        }`}
                        data-testid={`resource-assignments-total-${role}`}
                      >
                        {isOver && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
                        {total.toFixed(1)}% assigned
                      </span>
                      {candidates.length > 0 && !focusPersonId && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addRow(role)}
                          className="h-7 px-2 text-[11px]"
                          data-testid={`resource-assignments-add-${role}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                      )}
                    </div>
                  </div>

                  {assignedRows.length === 0 ? (
                    <p className="text-[11px] text-slate-400 italic">
                      No one assigned. {candidates.length > 0
                        ? `Click "Add" to pick from ${candidates.length} ${role} resource${candidates.length === 1 ? '' : 's'}.`
                        : `No active ${role} resources in the roster — add one in Settings → Named Resources.`}
                      {hrs > 0 && ` (${hrs.toLocaleString()}h will divide equally among peers via the fallback.)`}
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {assignedRows.map(row => {
                        const taken = rows.filter(o => o.role === role && o.key !== row.key).map(o => o.resource_id);
                        const available = candidates.filter(c => c.id === row.resource_id || !taken.includes(c.id));
                        const personHours = (hrs * (Number(row.percentage) || 0)) / 100;
                        const setWeeks = (n) => {
                          const wks = parseInt(n, 10);
                          const start = row.start_date || new Date().toISOString().slice(0, 10);
                          if (!wks || wks < 1) return;
                          const d = new Date(start + 'T00:00:00');
                          d.setDate(d.getDate() + wks * 7);
                          updateRow(row.key, { start_date: start, end_date: d.toISOString().slice(0, 10) });
                        };
                        return (
                          <div
                            key={row.key}
                            className="rounded-md border border-slate-100 bg-slate-50/40 p-2 space-y-1.5"
                            data-testid={`resource-assignments-row-${row.key}`}
                          >
                            <div className="flex items-center gap-2">
                              <select
                                value={row.resource_id}
                                onChange={e => updateRow(row.key, { resource_id: e.target.value })}
                                className="flex-1 border border-slate-200 rounded-md px-2 py-1 text-sm bg-white"
                                data-testid={`resource-assignments-resource-${row.key}`}
                              >
                                {available.map(c => (
                                  <option key={c.id} value={c.id}>
                                    {c.name}
                                    {c.email ? ` · ${c.email}` : ''}
                                  </option>
                                ))}
                              </select>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Input
                                  type="number"
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={row.percentage}
                                  onChange={e => updateRow(row.key, { percentage: e.target.value === '' ? 0 : Number(e.target.value) })}
                                  className="w-20 h-8 text-sm text-right tabular-nums"
                                  data-testid={`resource-assignments-pct-${row.key}`}
                                />
                                <span className="text-xs text-slate-400">% FTE</span>
                              </div>
                              {hrs > 0 && row.mode !== 'flat' && (
                                <span className="text-[11px] text-slate-500 tabular-nums w-20 text-right">
                                  ≈ {personHours.toFixed(0)}h
                                </span>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeRow(row.key)}
                                className="h-7 w-7 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                                data-testid={`resource-assignments-remove-${row.key}`}
                                title="Remove"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            {/* Time window + FTE mode */}
                            <div className="flex items-center gap-2 flex-wrap pl-0.5">
                              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid={`resource-assignments-mode-${row.key}`}>
                                {['planner', 'flat'].map(m => (
                                  <button
                                    key={m}
                                    type="button"
                                    onClick={() => updateRow(row.key, { mode: m })}
                                    className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${row.mode === m ? 'bg-[#0F2040] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                                    data-testid={`resource-assignments-mode-${m}-${row.key}`}
                                    title={m === 'planner' ? 'Slice the planner\'s hours within the window' : 'Fixed FTE commitment, independent of the effort curve'}
                                  >
                                    {m === 'planner' ? 'Planner slice' : 'Flat FTE'}
                                  </button>
                                ))}
                              </div>
                              <span className="text-[10px] text-slate-400">from</span>
                              <input
                                type="date"
                                value={row.start_date || ''}
                                onChange={e => updateRow(row.key, { start_date: e.target.value || null })}
                                className="h-7 border border-slate-200 rounded px-1.5 text-[11px] bg-white"
                                data-testid={`resource-assignments-start-${row.key}`}
                              />
                              <span className="text-[10px] text-slate-400">to</span>
                              <input
                                type="date"
                                value={row.end_date || ''}
                                onChange={e => updateRow(row.key, { end_date: e.target.value || null })}
                                className="h-7 border border-slate-200 rounded px-1.5 text-[11px] bg-white"
                                data-testid={`resource-assignments-end-${row.key}`}
                              />
                              <Input
                                type="number"
                                min={1}
                                placeholder="wks"
                                onChange={e => setWeeks(e.target.value)}
                                className="w-14 h-7 text-[11px] text-right"
                                data-testid={`resource-assignments-weeks-${row.key}`}
                                title="Quick-set: end = start + N weeks"
                              />
                              {(row.start_date || row.end_date) ? (
                                <button
                                  type="button"
                                  onClick={() => updateRow(row.key, { start_date: null, end_date: null })}
                                  className="text-[10px] text-slate-400 hover:text-rose-600 underline"
                                  data-testid={`resource-assignments-clear-dates-${row.key}`}
                                >
                                  whole project
                                </button>
                              ) : (
                                <span className="text-[10px] text-slate-400 italic">whole project</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!isOver && remaining > 0.1 && assignedRows.length > 0 && hrs > 0 && (
                    <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-1" data-testid={`resource-assignments-fallback-${role}`}>
                      <Info className="w-3 h-3" />
                      Remaining {remaining.toFixed(1)}% (≈ {fallbackHours.toFixed(0)}h) falls back to divide-equally across all {role} peers.
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          </div>
        )}
      </CardContent>

      {/* Person-centric bulk assign dialog */}
      <Dialog open={assignOpen} onOpenChange={(o) => { if (!assignBusy) setAssignOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="resource-assignments-assign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-[#4ABFCF]" />
              Assign a person to multiple roles
            </DialogTitle>
            <p className="text-xs text-slate-500">
              Pick someone and tick every role they'll cover on this project. Roles they don't
              already hold can be added to their profile inline. Rows are added to the planner —
              click <strong>Save</strong> to persist.
            </p>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-medium text-slate-600 block mb-1">Person</label>
              <select
                value={assignPersonId}
                onChange={(e) => setAssignPersonId(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white"
                data-testid="resource-assignments-assign-person-select"
              >
                {roster.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.email ? ` · ${p.email}` : ''} — holds: {resRoles(p).join(', ') || '—'}
                  </option>
                ))}
              </select>
            </div>

            <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-md divide-y divide-slate-50" data-testid="resource-assignments-assign-roles">
              {rolesInScope.length === 0 ? (
                <p className="text-xs text-slate-400 italic p-3 text-center">No roles in scope yet.</p>
              ) : rolesInScope.map(role => {
                const held = assignPerson ? resRoles(assignPerson).includes(role) : false;
                const sel = assignSel[role] || { selected: false, grant: false, pct: 100 };
                return (
                  <div key={role} className="flex items-center gap-2 px-3 py-2" data-testid={`resource-assignments-assign-role-${role}`}>
                    <input
                      type="checkbox"
                      checked={!!sel.selected}
                      onChange={(e) => toggleAssignRole(role, { selected: e.target.checked, grant: e.target.checked ? (held ? false : true) : false })}
                      className="w-4 h-4"
                      data-testid={`resource-assignments-assign-role-check-${role}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-[#0F2040] text-white text-[10px]">{role}</Badge>
                        <span className="text-sm text-[#0F2040] truncate">{roleLabels[role] || role}</span>
                      </div>
                      {!held && sel.selected && (
                        <label className="mt-1 flex items-center gap-1.5 text-[11px] text-amber-700 cursor-pointer select-none" data-testid={`resource-assignments-assign-grant-label-${role}`}>
                          <input
                            type="checkbox"
                            checked={!!sel.grant}
                            onChange={(e) => toggleAssignRole(role, { grant: e.target.checked })}
                            className="w-3.5 h-3.5"
                            data-testid={`resource-assignments-assign-grant-${role}`}
                          />
                          also add to {assignPerson?.name?.split(' ')[0] || 'their'}'s profile (not currently held)
                        </label>
                      )}
                    </div>
                    {sel.selected && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={sel.pct}
                          onChange={(e) => toggleAssignRole(role, { pct: e.target.value === '' ? 0 : Number(e.target.value) })}
                          className="w-16 h-8 text-sm text-right tabular-nums"
                          data-testid={`resource-assignments-assign-pct-${role}`}
                        />
                        <span className="text-xs text-slate-400">%</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAssignOpen(false)} disabled={assignBusy} data-testid="resource-assignments-assign-cancel">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={performAssignPerson}
              disabled={assignBusy}
              className="bg-[#0F2040] hover:bg-[#1A3055] text-white"
              data-testid="resource-assignments-assign-confirm"
            >
              {assignBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              Assign roles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy-from-previous-estimate dialog */}
      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent className="max-w-2xl" data-testid="resource-assignments-copy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-4 h-4 text-[#4ABFCF]" />
              Copy assignments from another estimate
            </DialogTitle>
            <p className="text-xs text-slate-500">
              Pick an estimate to import its resource assignments into this one. Rows whose resources have since gone inactive or changed role are skipped automatically.
            </p>
          </DialogHeader>
          {copyLoading ? (
            <div className="flex items-center justify-center py-10" data-testid="resource-assignments-copy-loading">
              <Loader2 className="w-5 h-5 animate-spin text-[#0F2040]" />
            </div>
          ) : copySource.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center" data-testid="resource-assignments-copy-empty">
              No other estimates with explicit assignments yet — add some via this same panel on another estimate first.
            </p>
          ) : (
            <>
              <label className="flex items-center gap-2 text-xs text-slate-700 select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={copyOverwrite}
                  onChange={e => setCopyOverwrite(e.target.checked)}
                  className="w-3.5 h-3.5"
                  data-testid="resource-assignments-copy-overwrite"
                />
                Overwrite this estimate's current assignments
                <span className="text-[10px] text-slate-400">(uncheck to merge: source roles replace target's same-role rows, untouched roles preserved)</span>
              </label>
              <div className="max-h-80 overflow-y-auto border border-slate-100 rounded-md divide-y divide-slate-50" data-testid="resource-assignments-copy-list">
                {copySource.map(e => (
                  <button
                    key={e.id}
                    type="button"
                    disabled={copying}
                    onClick={() => performCopy(e.id, e.name)}
                    data-testid={`resource-assignments-copy-pick-${e.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-[#4ABFCF]/5 flex items-center justify-between gap-3 transition disabled:opacity-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-[#0F2040] truncate">{e.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {e.organization_name && <span>{e.organization_name} · </span>}
                        {e.assignment_count} row{e.assignment_count === 1 ? '' : 's'} · roles: {e.roles.join(', ')}
                        {e.updated_at && <> · saved {new Date(e.updated_at).toLocaleDateString()}</>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCopyOpen(false)} disabled={copying} data-testid="resource-assignments-copy-close">
              {copying ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
